import { useEffect, useState } from 'react';
import Spinner from 'react-spinkit';
import SortableTable from '../Tables/SortableTable';
import {
  getTotalRow,
  getNumberOfStudents,
  getLegacyEducatorLogins,
  getNewEducatorLogins,
  getLegacyStudentLogins,
  getNewStudentLogins,
  getLegacyLessonsAccessed,
  getNewLessonsAccessed,
  getNumberOfEducators,
  getLegacyStoryOpens,
  getNewStoryOpens,
  getLegacyTimeOnPlatform,
  getNewTimeOnPlatform,
} from '../reportUtilities';
import styles from './TableStyles.module.scss';
import { ReportStudent, ReportingMixpanelEvent } from '@peekapak/types/ReportDataExchangeTypes';
import { UserType } from '@peekapak/types/DataProtocolTypes';

interface Props {
  legacyPlatformEvents: ReportingMixpanelEvent[];
  legacyPlatformEventsReady: boolean;
  errorMessage: string;
  legacyPeekavilleEvents: ReportingMixpanelEvent[];
  legacyPeekavilleEventsReady: boolean;
  newMixpanelEvents: ReportingMixpanelEvent[];
  newMixpanelEventsReady: boolean;
  teacherStudentMap: Record<string, ReportStudent[]>;
  teacherStudentMapReady: boolean;
}

interface DataMap {
  Staff: ReportingMixpanelEvent[];
  Students: ReportingMixpanelEvent[];
}

interface MyState {
  dataMap: DataMap;
  dataMapReady: boolean;
}

/* eslint-disable complexity */
const AnalyticsOverview = ({
  legacyPlatformEvents,
  legacyPlatformEventsReady,
  errorMessage,
  legacyPeekavilleEvents,
  legacyPeekavilleEventsReady,
  newMixpanelEvents,
  newMixpanelEventsReady,
  teacherStudentMap,
  teacherStudentMapReady,
}: Props): JSX.Element => {
  const [myState, setMyState] = useState<MyState>({
    dataMap: { Staff: [], Students: [] },
    dataMapReady: false,
  });

  useEffect(() => {
    setMyState((prevState) => ({
      ...prevState,
      dataMap: { Staff: [], Students: [] },
      dataMapReady: false,
    }));
  }, [legacyPlatformEvents, legacyPeekavilleEvents, newMixpanelEvents]);

  useEffect(() => {
    if (
      legacyPlatformEventsReady &&
      legacyPeekavilleEventsReady &&
      newMixpanelEventsReady &&
      teacherStudentMapReady &&
      !myState.dataMapReady
    ) {
      buildData();
    }

    function buildData() {
      const newMap: DataMap = {
        Staff: [],
        Students: [],
      };

      newMap['Staff'] = [...legacyPlatformEvents, ...newMixpanelEvents].filter(
        (item) =>
          item.type === UserType.TEACHER ||
          item.type === UserType.SCHOOL_ADMIN ||
          item.type === UserType.DISTRICT_ADMIN,
      );

      newMap['Students'] = [...legacyPlatformEvents, ...legacyPeekavilleEvents, ...newMixpanelEvents].filter(
        (event) => {
          return event.type === UserType.STUDENT;
        },
      );

      setMyState((prevState) => ({
        ...prevState,
        dataMap: newMap,
        dataMapReady: true,
      }));
    }
  }, [
    legacyPeekavilleEventsReady,
    legacyPeekavilleEvents,
    myState.dataMapReady,
    legacyPlatformEvents,
    legacyPlatformEventsReady,
    newMixpanelEvents,
    newMixpanelEventsReady,
    teacherStudentMap,
    teacherStudentMapReady,
  ]);

  const message = getDataNotReadyMessage();
  if (message) {
    return message;
  }

  const staticMessage = getStaticMessage();
  if (staticMessage) {
    return message;
  }

  const userTypes = ['Staff', 'Students'];
  const rows = userTypes.map((userType) => {
    return {
      user_type: userType,
      active_users: getNumberofUsers(userType),
      user_logins: getNumberofUserLogins(userType),
      lessons_accessed: getNumberOfLessonsAccessed(userType),
      story_opens: getNumberOfStoryOpens(userType),
      time_on_platform: getTimeOnPlatform(userType),
    };
  });

  const tableData = rows.map((e) => {
    return {
      user_type: e.user_type,
      active_users: e.active_users,
      user_logins: e.user_logins,
      lessons_accessed: e.lessons_accessed,
      story_opens: e.story_opens,
      time_on_platform: e.time_on_platform,
    };
  });

  const columns = [
    {
      label: 'User Type',
      accessor: 'user_type',
      sortable: false,
    },
    {
      label: 'Active Users',
      accessor: 'active_users',
      sortable: true,
      tooltip:
        'Number of users who have logged in at least once during the time period. Note that users who no longer have accounts may be included in this number',
    },

    {
      label: 'User Logins',
      accessor: 'user_logins',
      sortable: true,
      tooltip:
        'Total logins by a user of this type during the time period. Note that users who no longer have accounts or have been de-rostered may be included in this number',
    },
    {
      label: 'Lessons Accessed',
      accessor: 'lessons_accessed',
      sortable: true,
      tooltip: 'Number of times a teacher has accessed a lesson during the time period',
    },
    {
      label: 'Story Opens',
      accessor: 'story_opens',
      sortable: true,
      tooltip:
        'Number of times an elementary storybook or a secondary school human interest story was accessed by a user at this school',
    },
    {
      label: 'Time on Platform (hrs)',
      accessor: 'time_on_platform',
      sortable: true,
    },
  ];

  const totalRow = getTotalRow(tableData);

  return (
    <div>
      <SortableTable data={tableData} columns={columns} totals={totalRow} />
    </div>
  );

  function getStaticMessage() {
    if (errorMessage) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>
            {`We're sorry, there's a problem retrieving data. Please
            contact Peekapak support and provide them with this error message:`}
            {errorMessage}
          </div>
        </div>
      );
    }
  }

  function getDataNotReadyMessage() {
    let message = null;

    if (!myState.dataMapReady) {
      message = 'Calculating stats...';
    }

    if (message) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>
            <Spinner name='line-scale' />
            This may take up to 10 minutes or more
          </div>
        </div>
      );
    }

    return null;
  }

  function getNumberofUsers(type: string) {
    const array = myState.dataMap[type as keyof DataMap];
    if (type === 'Staff') {
      return getNumberOfEducators(array);
    } else if (type === 'Students') {
      return getNumberOfStudents(array);
    }
  }

  function getNumberofUserLogins(type: string) {
    const userArray = myState.dataMap[type as keyof DataMap];
    if (type === 'Staff') {
      return getLegacyEducatorLogins(userArray) + getNewEducatorLogins(userArray);
    }
    if (type === 'Students') {
      return getLegacyStudentLogins(userArray) + getNewStudentLogins(userArray);
    }
  }

  function getNumberOfLessonsAccessed(type: string) {
    const array = myState.dataMap[type as keyof DataMap];
    if (type === 'Staff') {
      return getLegacyLessonsAccessed(array) + getNewLessonsAccessed(array);
    } else if (type === 'Students') {
      return 'N/A';
    }
  }

  function getTimeOnPlatform(type: string) {
    const array = myState.dataMap[type as keyof DataMap];
    return getLegacyTimeOnPlatform(array) + getNewTimeOnPlatform(array);
  }

  function getNumberOfStoryOpens(type: string) {
    const array = myState.dataMap[type as keyof DataMap];
    return getLegacyStoryOpens(array) + getNewStoryOpens(array);
  }
};

export default AnalyticsOverview;
